"use client";

import staticData from "@/public/data/flight/results.json";
import noResultsImage from "@/public/svg/No-Flights.svg";
import Flex from "@/shared/components/common/Flex";
import List from "@/shared/components/common/List";
import type { Flight } from "@/types/services/flight/apis/Availability";
import FlightCard from "./flightCard";
import NoResult from "@/containers/components/NoResult";
import { usePagination } from "@/shared/hooks/usePagination";
import { useMemo, useRef } from "react";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { filterFlights } from "@/shared/libs/filter-utils";
import { useClientSearchParams } from "@/shared/hooks/useClientSearchParams";

interface FlightListProps {
  searchParams: SearchParams;
  flights: Flight[];
}

export default function FlightList({ searchParams, flights }: FlightListProps) {
  const deviceType = useDeviceType();
  const filterSearchParams = useClientSearchParams();

  const ref = useRef(null);

  const { title, subtitle } = staticData.noResults;

  const filteredFlights = useMemo(
    () =>
      filterFlights(flights, {
        sort: filterSearchParams.get("sort") ?? undefined,
        price: filterSearchParams.get("price") ?? undefined,
        query: filterSearchParams.get("query") ?? undefined,
        airline: filterSearchParams.get("airline") ?? undefined,
        departureTime: filterSearchParams.get("departureTime") ?? undefined,
        classes: filterSearchParams.get("classes") ?? undefined,
        cabinType: filterSearchParams.get("cabinType") ?? undefined,
      }),
    [filterSearchParams, flights],
  );

  const paginatedFlights = usePagination<Flight>(filteredFlights, ref, null, {
    pageLimit: deviceType === "mobile" ? 2 : 4,
    debounce: 500,
  });

  return (
    <Flex direction={"col"} className="space-y-4">
      {paginatedFlights.length === 0 && (
        <NoResult title={title} description={subtitle} image={noResultsImage} />
      )}

      <List
        items={paginatedFlights}
        renderItems={(flight) => (
          <FlightCard searchParams={searchParams} flight={flight} />
        )}
      />

      <div ref={ref} />
    </Flex>
  );
}
